/**
 * Temporary raw-fetch compatibility boundary.
 *
 * Authenticated backend traffic should go through `getBackendClient()` /
 * domain API modules in `api/*.ts`. This plugin wraps `globalThis.fetch` so
 * any residual direct backend `fetch` still gets the same MFA redirect and
 * impersonation-session behavior.
 *
 * Deletion criterion:
 * Delete this plugin after every authenticated backend call uses
 * `getBackendClient()` (or a domain module that does). At that point the typed
 * client middleware alone owns auth, impersonation, and MFA policy.
 *
 * Do not reimplement MFA or impersonation here. Shared helpers live in
 * `utils/backendFetch.ts` and are also used by `api/client.ts`.
 */
import {
  applyImpersonationSessionHeaders,
  isBackendRequest,
  notifyImpersonationSessionExpired,
  wrapFetchWithBackendInterceptors,
} from '~/utils/backendFetch'

const withImpersonationHeader = (
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  backendBasePath: string,
  impersonationExpiredMessage: string | undefined,
) => {
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))

  const requestUrl = input instanceof Request ? input.url : input.toString()
  if (!isBackendRequest(requestUrl, backendBasePath)) {
    return { input, init }
  }

  const result = applyImpersonationSessionHeaders(headers)
  notifyImpersonationSessionExpired(result.expiredCleared, impersonationExpiredMessage)

  if (input instanceof Request) {
    return {
      input: new Request(input, {
        ...init,
        headers,
      }),
      init: undefined,
    }
  }

  return {
    input,
    init: {
      ...init,
      headers,
    },
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const backendBasePath = config.public.BACKEND_URL as string
  const i18n = nuxtApp.$i18n as { t: (key: string) => string } | undefined
  const enrollmentMessage = i18n?.t('auth.mfa.enrollmentRequiredRedirect')
  const impersonationExpiredMessage = i18n?.t('impersonate.sessionExpired')

  if (import.meta.client) {
    const nativeFetch = globalThis.fetch.bind(globalThis)
    if (!(nativeFetch as { __backendIntercepted?: boolean }).__backendIntercepted) {
      const interceptedFetch = wrapFetchWithBackendInterceptors(
        async (input, init) => {
          const request = withImpersonationHeader(
            input,
            init,
            backendBasePath,
            impersonationExpiredMessage,
          )
          return nativeFetch(request.input, request.init)
        },
        backendBasePath,
        enrollmentMessage,
      )
      ;(interceptedFetch as { __backendIntercepted?: boolean }).__backendIntercepted = true
      globalThis.fetch = interceptedFetch
    }
  }
})
